import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { TileEmpty } from '../TileWrapper';
import { Calendar, Clock } from 'lucide-react';
import { format, isToday, isTomorrow, addDays } from 'date-fns';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = [
  { id: '1', title: 'Complete site induction', due_date: new Date().toISOString().split('T')[0], project_id: '1', projects: { name: 'Site Alpha' }, assignees: [{ id: '1', first_name: 'John', last_name: 'Smith', avatar_url: null }] },
  { id: '2', title: 'Submit risk assessment', due_date: new Date().toISOString().split('T')[0], project_id: '1', projects: { name: 'Site Alpha' }, assignees: [{ id: '2', first_name: 'Sarah', last_name: 'Jones', avatar_url: null }, { id: '3', first_name: 'Mike', last_name: 'Brown', avatar_url: null }] },
  { id: '3', title: 'Review method statements', due_date: new Date(Date.now() + 86400000).toISOString().split('T')[0], project_id: '2', projects: { name: 'Main Office' }, assignees: [{ id: '1', first_name: 'John', last_name: 'Smith', avatar_url: null }] },
  { id: '4', title: 'Update permit to work', due_date: new Date(Date.now() + 172800000).toISOString().split('T')[0], project_id: '1', projects: { name: 'Site Alpha' }, assignees: [] },
  { id: '5', title: 'Schedule toolbox talk', due_date: new Date(Date.now() + 259200000).toISOString().split('T')[0], project_id: '3', projects: { name: 'Warehouse B' }, assignees: [{ id: '4', first_name: 'Emma', last_name: 'Wilson', avatar_url: null }] },
  { id: '6', title: 'Complete LOLER inspection', due_date: new Date(Date.now() + 345600000).toISOString().split('T')[0], project_id: '2', projects: { name: 'Main Office' }, assignees: [{ id: '2', first_name: 'Sarah', last_name: 'Jones', avatar_url: null }] },
];

export function TasksDueSoonTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(size === 'tall' ? MOCK_DATA : MOCK_DATA.slice(0, 5));
      setLoading(false);
      return;
    }

    if (!company?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];
        const nextWeek = addDays(new Date(), 7).toISOString().split('T')[0];

        let query = supabase
          .from('tasks')
          .select(`
            id,
            title,
            status,
            due_date,
            assigned_to,
            project_id,
            projects!inner(name, company_id)
          `)
          .eq('projects.company_id', company.id)
          .neq('status', 'done')
          .gte('due_date', today)
          .lte('due_date', nextWeek)
          .order('due_date', { ascending: true })
          .limit(size === 'tall' ? 10 : 6);

        if (filter?.spaceId) {
          query = query.eq('project_id', filter.spaceId);
        }

        const { data: tasks, error } = await query;

        if (error) throw error;

        // Get assignee details
        const assigneeIds = new Set();
        tasks?.forEach(t => t.assigned_to?.forEach(id => assigneeIds.add(id)));

        let assignees = {};
        if (assigneeIds.size > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name, avatar_url')
            .in('id', Array.from(assigneeIds));

          profiles?.forEach(p => {
            assignees[p.id] = p;
          });
        }

        const enrichedTasks = (tasks || []).map(t => ({
          ...t,
          assignees: (t.assigned_to || []).map(id => assignees[id]).filter(Boolean),
        }));

        setData(enrichedTasks);
      } catch (err) {
        console.error('Failed to load due soon tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, size, useMockData]);

  const formatDueDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-gray-200" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return <TileEmpty message="No tasks due this week" icon={Calendar} />;
  }

  // Group by date
  const grouped = data.reduce((acc, task) => {
    const key = task.due_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(task);
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([date, tasks]) => (
        <div key={date}>
          <p className="text-xs font-medium text-gray-500 mb-1">
            {formatDueDate(date)}
          </p>
          <div className="space-y-1">
            {tasks.map((task) => (
              <div
                key={task.id}
                onClick={() => navigate(`/space/${task.project_id}/tasks`)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{task.title}</p>
                  <p className="text-xs text-gray-500 truncate">{task.projects?.name}</p>
                </div>

                {task.assignees.length > 0 && (
                  <div className="flex -space-x-1">
                    {task.assignees.slice(0, 2).map((a) => (
                      <Avatar key={a.id} className="h-6 w-6 border-2 border-white">
                        <AvatarImage src={a.avatar_url} />
                        <AvatarFallback className="text-xs">
                          {a.first_name?.[0]}{a.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {task.assignees.length > 2 && (
                      <div className="h-6 w-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs">
                        +{task.assignees.length - 2}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
